// ── Init & Input Setup ────────────────────────────────────────────────────
// Contains init() and setupInput(), extracted from index.html (Task 30).

import { state, filters } from '../state';
import { dom } from '../dom';
import { APP_DATA } from '../appData';
import {
  CX, CY,
  GYRO_MAX_TILT_DEG,
  PERSISTENT_CLOCK_ENABLED,
  INSTALLER_STATES,
  ORGANISM_PAIR_GAP,
  HOME_OPTIONS_HOLD_MS,
} from '../config';
import { clamp } from '../utils/math';
import {
  quatInverse, quatMultiply, quatSlerp,
  quatToDeviceRotation, loopQuatToArray,
} from '../utils/quaternion';
import { loop, hasLoop, hasMotion, hasButtons, hasHaptics, hasSystemFreeRotate } from '../loopSdk';
import {
  setSelectItemRef,
  primeSettledScales,
  isScannerInteractiveState,
} from '../layout/ringLayout';
import {
  createItems,
  setAppScaleBoostByName,
  clearAppScaleBoosts,
  setAppAnchorByName,
  setInstallerScannerDimOpacity,
  setInstallerScannerDimStrength,
} from '../items/createItems';
import {
  setResetInstallerScannerVisualRef,
} from '../render/lineRenderer';
import {
  setResetToLockRef,
  setSelectItemForScramble,
  handleHomePrimaryAction,
} from '../render/matrixScramble';
import {
  canStartHomeOptions,
  startHomeOptionsFromHighlight,
  handleHomeBackAction,
  handleHomeSecondarySelectAction,
  endHomeEditDrag,
  nudgeHomeOptionSelection,
  updateHomeOptionTwistFromMotion,
  getSelectedOptionRowElement,
} from '../home/homeOptions';
import { getSceneRect, initSceneViewport } from '../layout/sceneViewport';
import {
  setStartUnlockTransitionRef,
  handleUnlockPress,
} from '../lock/unlockPress';
import {
  startUnlockTransition,
  resetToLock,
  startLockingTransition,
  resetInstallerScannerVisual,
  cancelInstallerFlow,
  triggerCompatibleInstall,
  setSetStateRef,
  runItemSelectionAction,
} from '../lock/unlockTransition';
import {
  selectItem,
  setRunItemSelectionActionRef,
} from '../selection/launch';
import {
  beginPress,
  endPress,
  pressItem,
  pressElement,
  pressClock,
} from '../items/pressFeedback';
import { isHomeCenterZoneActive } from '../render/matrixScramble';
import { initWatchface, setState } from './appStateMachine';
import { startMainLoop } from './mainLoop';

const maxTiltRad = GYRO_MAX_TILT_DEG * Math.PI / 180;

function recenterCamera(): void {
  state.cameraNormX = 0; state.cameraNormY = 0;
  state.cameraTargetX = 0; state.cameraTargetY = 0;
  // Recalibrate home gyro — next frame becomes new center
  state.homeRefQuat = null;
  filters.cameraX.reset(); filters.cameraY.reset();
  if (hasHaptics) loop!.haptics!.pulse(0.3);
  console.log('[Loop OS] Camera recentered, gyro recalibrated');
}

function setupInput(): void {
  let optionMouseLastY: number | null = null;
  let optionMouseDeltaAccum = 0;
  const OPTION_MOUSE_STEP_PX = 14;

  dom.scene.addEventListener('mousemove', e => {
    if (state.appState === 'lock') {
      const rect = getSceneRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      state.parallaxTargetX = clamp((mx - CX) / CX, -1, 1);
      state.parallaxTargetY = clamp((my - CY) / CY, -1, 1);
    }
  });

  // Desktop Home camera control: track pointer globally so you can keep panning
  // even when cursor goes outside the circular viewport while debugging.
  // L1 input — reads scene rect through SceneViewport's cache, never live DOM.
  window.addEventListener('mousemove', e => {
    if (state.appState !== 'home') {
      optionMouseLastY = null;
      optionMouseDeltaAccum = 0;
      return;
    }
    const now = performance.now() / 1000;
    const rect = getSceneRect();
    const centerX = rect.left + rect.width * 0.5;
    const centerY = rect.top + rect.height * 0.5;
    const halfW = Math.max(1, rect.width * 0.5);
    const halfH = Math.max(1, rect.height * 0.5);
    state.cameraNormX = filters.cameraX.filter(
      clamp((e.clientX - centerX) / halfW, -1, 1), now
    );
    state.cameraNormY = filters.cameraY.filter(
      clamp((e.clientY - centerY) / halfH, -1, 1), now
    );
    state.inputActive = true;
    if (state.homeUI.mode === 'options_menu') {
      const fallbackDeltaY = optionMouseLastY == null ? 0 : (e.clientY - optionMouseLastY);
      const deltaY = Number.isFinite(e.movementY) && Math.abs(e.movementY) > 0 ? e.movementY : fallbackDeltaY;
      if (optionMouseLastY == null) {
        optionMouseLastY = e.clientY;
        optionMouseDeltaAccum = 0;
        return;
      }
      optionMouseLastY = e.clientY;
      // Direct mapping: accumulator is bounded so phase never exceeds ±1,
      // and the commit picks the option whose zone the phase falls in.
      optionMouseDeltaAccum = clamp(
        optionMouseDeltaAccum + deltaY,
        -OPTION_MOUSE_STEP_PX,
        OPTION_MOUSE_STEP_PX,
      );
      state.homeUI.optionsInputPhase = clamp(
        optionMouseDeltaAccum / OPTION_MOUSE_STEP_PX, -1, 1,
      );
      return;
    }
    optionMouseLastY = null;
    optionMouseDeltaAccum = 0;
  });

  dom.scene.addEventListener('mouseleave', () => {
    if (state.appState === 'lock') { state.parallaxTargetX = 0; state.parallaxTargetY = 0; }
    optionMouseLastY = null;
    optionMouseDeltaAccum = 0;
    // Home camera: keep last values — no snap to center
  });

  // Click on lock screen = unlock press (desktop)
  dom.scene.addEventListener('pointerdown', e => {
    if (state.appState === 'lock') { e.preventDefault(); handleUnlockPress(); }
  });

  // Desktop keys: H=A button, T=B button
  // Hold H (or hardware A) to recenter camera + recalibrate gyro.
  // Hold T (or hardware B) to open app options.
  let hHoldTimer: ReturnType<typeof setTimeout> | 'fired' | null = null;
  let tHoldTimer: ReturnType<typeof setTimeout> | 'fired' | null = null;
  // Action to fire on key/button release if the press still represents an
  // uncommitted intent (i.e., the hold timer hasn't already opened options
  // and edit-mode hasn't intercepted). Set in *Down, consumed in *Up.
  let pendingPressAction: (() => void) | null = null;
  const HOLD_MS = 400;

  function clearTHoldTimer(): void {
    if (tHoldTimer && tHoldTimer !== 'fired') clearTimeout(tHoldTimer as ReturnType<typeof setTimeout>);
    tHoldTimer = null;
  }

  function handleHomeSecondaryDown(): void {
    pendingPressAction = null;
    if (state.appState !== 'home') return;
    const ui = state.homeUI;

    // Options menu: press the currently selected row, fire its action on
    // release. The visual feedback distinguishes "I'm pressing" from
    // "selection moved here" purely from the SVG connector position.
    if (ui.mode === 'options_menu') {
      const targetEl = getSelectedOptionRowElement();
      if (targetEl) beginPress(pressElement(targetEl));
      pendingPressAction = () => { handleHomeSecondarySelectAction(); };
      return;
    }

    // Edit mode: secondary down begins drag; no scale-press feedback (the
    // drag itself is the visual feedback).
    if (ui.mode === 'edit_mode') {
      handleHomeSecondarySelectAction();
      return;
    }

    // Normal / options_exiting: arm the primary action (launch highlighted
    // app, OR re-lock when the cursor is on the center clock — handled
    // inside handleHomePrimaryAction via isHomeCenterZoneActive). Visual
    // press feedback fires on whichever target the press maps to: the
    // highlighted icon if there is one, else the center clock when the
    // cursor is sitting on the lock zone.
    if (ui.mode === 'normal' || ui.mode === 'options_exiting') {
      if (state.highlighted) {
        beginPress(pressItem(state.highlighted));
      } else if (isHomeCenterZoneActive()) {
        beginPress(pressClock());
      }
      pendingPressAction = handleHomePrimaryAction;
    }

    clearTHoldTimer();
    tHoldTimer = setTimeout(() => {
      tHoldTimer = 'fired';
      if (canStartHomeOptions() && startHomeOptionsFromHighlight()) {
        // Options is taking over — release the press visually so the icon
        // pops back as the menu zooms in. Cancel the pending launch action.
        endPress();
        pendingPressAction = null;
      }
    }, HOME_OPTIONS_HOLD_MS);
  }

  function handleHomeSecondaryUp(): void {
    const wasHoldFired = tHoldTimer === 'fired';
    clearTHoldTimer();
    // Always end the press if one is active. The rebound (pop) plays
    // independently of whatever action runs.
    endPress();

    if (state.homeUI.mode === 'edit_mode') {
      endHomeEditDrag();
      pendingPressAction = null;
      return;
    }

    if (!wasHoldFired && pendingPressAction) {
      const action = pendingPressAction;
      pendingPressAction = null;
      action();
      return;
    }
    pendingPressAction = null;
  }

  document.addEventListener('keydown', e => {
    if (e.repeat) return;
    const k = e.key.toLowerCase();
    if (state.appState === 'home' && state.homeUI.mode === 'options_menu') {
      if (k === 'arrowleft' || k === 'arrowup' || k === 'j') {
        nudgeHomeOptionSelection(-1);
        return;
      }
      if (k === 'arrowright' || k === 'arrowdown' || k === 'l') {
        nudgeHomeOptionSelection(1);
        return;
      }
    }
    if (k === 'h' && state.appState === 'home' && handleHomeBackAction()) return;
    if (state.appState === 'home' && isScannerInteractiveState()) {
      if (k === 'h') {
        cancelInstallerFlow();
        return;
      }
      if (k === 'c') {
        triggerCompatibleInstall();
        return;
      }
    }
    if (k === 'h') {
      if (state.appState === 'lock') {
        handleUnlockPress();
      } else if (state.appState === 'home') {
        // Start hold timer — if held long enough, recenter
        hHoldTimer = setTimeout(() => { hHoldTimer = 'fired'; recenterCamera(); }, HOLD_MS);
      }
    }
    if (k === 't') {
      if (state.appState === 'lock' && state.watchfaceHost) state.watchfaceHost.triggerSwap();
      else if (state.appState === 'home') handleHomeSecondaryDown();
    }
  });

  document.addEventListener('keyup', e => {
    const k = e.key.toLowerCase();
    if (k === 'h') {
      if (hHoldTimer && hHoldTimer !== 'fired') clearTimeout(hHoldTimer as ReturnType<typeof setTimeout>);
      hHoldTimer = null;
    }
    if (k === 't' && state.appState === 'home') handleHomeSecondaryUp();
  });

  // Gyro — separate reference quaternions for lock vs home (matches Cards OS)
  if (hasMotion) {
    loop!.motion!.start({ frequency: 60, smoothing: 0.25 }).then(sub => {
      sub.on('data', data => {
        if (!data.orientation) return;
        const now = performance.now() / 1000;
        const q = loopQuatToArray(data.orientation);

        // Lock screen parallax — uses state.refQuat + OneEuroFilter
        if (state.appState === 'lock') {
          if (!state.refQuat) { state.refQuat = q; return; }
          const rel = quatMultiply(quatInverse(state.refQuat!), q);
          const { pitch, yaw } = quatToDeviceRotation(rel);
          let rawX = -yaw / maxTiltRad;
          let rawY = -pitch / maxTiltRad;
          const absX = Math.abs(rawX), absY = Math.abs(rawY);
          if (absX > 1 || absY > 1) {
            const excess = Math.max(absX - 1, absY - 1, 0);
            const absorbRate = Math.min(0.5, excess * 0.3);
            state.refQuat = quatSlerp(state.refQuat!, q, absorbRate);
            const updRel = quatMultiply(quatInverse(state.refQuat), q);
            const upd = quatToDeviceRotation(updRel);
            rawX = clamp(-upd.yaw / maxTiltRad, -1, 1);
            rawY = clamp(-upd.pitch / maxTiltRad, -1, 1);
          }
          state.parallaxTargetX = filters.inputX.filter(clamp(rawX, -1, 1), now);
          state.parallaxTargetY = filters.inputY.filter(clamp(rawY, -1, 1), now);
          if (state.watchfaceHost) state.watchfaceHost.onMotion(data);

        // Home camera — uses state.homeRefQuat, no OneEuroFilter (matches Cards OS grid camera)
        } else if (state.appState === 'home') {
          if (!state.homeRefQuat) {
            state.homeRefQuat = q;
            updateHomeOptionTwistFromMotion(q, now);
            return;
          }
          const rel = quatMultiply(quatInverse(state.homeRefQuat!), q);
          const { pitch, yaw } = quatToDeviceRotation(rel);
          const rawX = -yaw / maxTiltRad;
          const rawY = -pitch / maxTiltRad;
          const absX = Math.abs(rawX), absY = Math.abs(rawY);
          let normX, normY;
          if (absX > 1 || absY > 1) {
            const excess = Math.max(absX - 1, absY - 1, 0);
            const absorbRate = Math.min(0.5, excess * 0.3);
            state.homeRefQuat = quatSlerp(state.homeRefQuat!, q, absorbRate);
            const updRel = quatMultiply(quatInverse(state.homeRefQuat), q);
            const upd = quatToDeviceRotation(updRel);
            normX = clamp(-upd.yaw / maxTiltRad, -1, 1);
            normY = clamp(-upd.pitch / maxTiltRad, -1, 1);
          } else {
            normX = clamp(rawX, -1, 1);
            normY = clamp(rawY, -1, 1);
          }
          state.cameraNormX = normX;
          state.cameraNormY = normY;
          state.inputActive = true;
          updateHomeOptionTwistFromMotion(q, now);
        }
      });
    }).catch(err => console.warn('[Loop OS] Motion failed:', err));
  }

  // Hardware buttons — SWAPPED from Grid OS
  let aHoldTimer: ReturnType<typeof setTimeout> | 'fired' | null = null;
  if (hasButtons) {
    loop!.buttons!.on('A', e => {
      if (e.state === 'down') {
        if (state.appState === 'home' && handleHomeBackAction()) return;
        if (state.appState === 'home' && isScannerInteractiveState()) {
          cancelInstallerFlow();
          return;
        }
        if (state.appState === 'lock') {
          handleUnlockPress();
        } else if (state.appState === 'home') {
          aHoldTimer = setTimeout(() => { aHoldTimer = 'fired'; recenterCamera(); }, HOLD_MS);
        }
      } else if (e.state === 'up') {
        if (aHoldTimer && aHoldTimer !== 'fired') clearTimeout(aHoldTimer as ReturnType<typeof setTimeout>);
        aHoldTimer = null;
      }
    });
    loop!.buttons!.on('B', e => {
      if (e.state === 'down') {
        if (state.appState === 'lock' && state.watchfaceHost) state.watchfaceHost.triggerSwap();
        else if (state.appState === 'home') handleHomeSecondaryDown();
      } else if (e.state === 'up') {
        if (state.appState === 'home') handleHomeSecondaryUp();
      }
    });
    loop!.buttons!.on('C', e => {
      if (e.state === 'down') {
        if (state.appState === 'home' && isScannerInteractiveState()) {
          triggerCompatibleInstall();
          return;
        }
        if ((state.appState === 'lock' || state.appState === 'home') && hasLoop) loop!.system!.quit();
      }
    });
  }

  dom.relockCapture.addEventListener('pointerdown', e => {
    e.preventDefault();
    e.stopPropagation();
    if (PERSISTENT_CLOCK_ENABLED && state.appState === 'home') startLockingTransition();
  });
}

// Turn on Loop's native display auto-level (free rotate) so the OS stays
// visually upright regardless of how the user holds the device. This is a
// compositor-level transform — the WebView canvas remains hardware-fixed,
// so Matter.js gravity continues to work without any compensation.
function enableFreeRotateIfAvailable(): void {
  if (!hasSystemFreeRotate) return;
  try {
    const result = loop!.system!.setFreeRotate(true);
    if (!result.success) {
      console.warn('[Loop OS] setFreeRotate(true) returned success=false');
    } else {
      console.log('[Loop OS] free rotate enabled, state =', loop!.system!.isFreeRotateEnabled());
    }
  } catch (err) {
    console.warn('[Loop OS] setFreeRotate threw', err);
  }
}

export function init(): void {
  // Wire selectItem callback into ringLayout.ts.
  setSelectItemRef(selectItem);
  // Wire resetInstallerScannerVisual callback into lineRenderer.ts.
  setResetInstallerScannerVisualRef(resetInstallerScannerVisual);
  // Wire resetToLock and selectItem callbacks into matrixScramble.ts.
  setResetToLockRef(resetToLock);
  setSelectItemForScramble(selectItem);
  // Wire startUnlockTransition forward-ref into unlockPress.ts.
  setStartUnlockTransitionRef(startUnlockTransition);
  // Wire setState forward-ref into unlockTransition.ts.
  // Cast through `any` because setSetStateRef expects (string) => void while
  // setState is typed to the narrower AppState union — the call site only
  // ever passes valid AppState values.
  setSetStateRef(setState as (newState: string) => void);
  // Wire runItemSelectionAction forward-ref into launch.ts. The press
  // animation is no longer invoked from selectItem — the keyboard handler
  // owns the press visual via the pressFeedback module.
  setRunItemSelectionActionRef(runItemSelectionAction);

  // Turn on native free-rotate before we touch the scene, so the first
  // painted frame is already in the correct orientation.
  enableFreeRotateIfAvailable();

  // Initialize installerFlow state object.
  state.installerFlow = {
    state: INSTALLER_STATES.IDLE,
    centeringStartedAt: 0,
    scannerMessageUntil: 0,
    scannerMessageReset: false,
    scannerCloseStartedAt: 0,
    scannerObstacleGap: ORGANISM_PAIR_GAP,
    requestToken: 0,
    pendingInstallPayload: null,
    installItem: null,
    installDoneAt: 0,
  };

  // Kick off cover-image decoding in parallel with the rest of init. The
  // <link rel="preload" as="image"> hints in index.html have already
  // started the HTTP fetches during HTML parse; img.decode() here waits
  // until each image is actually decoded into the browser's image cache.
  //
  // Without this, first-unlock on low-end hardware (e.g. the Loop device)
  // stalls for 100-500 ms as the compositor has to cold-decode each cover
  // PNG the first time the item it belongs to becomes visible — the "pop"
  // the user reported.
  const coversReady = preloadCovers();

  createItems();
  primeSettledScales();

  if (typeof window !== 'undefined') {
    // `window.loopOrganism` is a debug/testing escape hatch — not part of any typed API.
    (window as unknown as Record<string, unknown>).loopOrganism = {
      setAppScaleBoostByName,
      clearAppScaleBoosts,
      setAppAnchorByName,
      setInstallerScannerDimOpacity,
      setInstallerScannerDimStrength,
    };
    // DEV/test debug hatch: expose the live state object for instrumented
    // playwright probes to inspect homeUI mode, dragLockedScale, etc. Not
    // part of any typed API; do not depend on this in production code.
    (window as unknown as Record<string, unknown>).__loopState = state;
  }

  initSceneViewport();
  initWatchface();
  setupInput();

  // Wait for cover decodes before exposing the lock screen so the very
  // first unlock lands on textures that are already in the browser's
  // image cache. GPU texture upload from a decoded image is cheap
  // (~1 ms per cover), so this is the last guard we need for the cold path.
  coversReady
    .catch(err => {
      // A single failed cover must not block boot — fall through to showing
      // lock screen anyway; the offending item will just render its color
      // fallback until its PNG eventually loads.
      console.warn('[Loop OS] cover preload partial failure:', err);
    })
    .then(() => {
      // Two-frame GPU warmup: render items at near-zero opacity for a frame
      // so the compositor creates a hardware layer + uploads the cover
      // textures, THEN snap back to opacity 0 for the lock screen. Items
      // are behind the watchface overlay in stacking order, and 0.01
      // opacity for one rAF is imperceptible.
      for (const item of state.items) item.el.style.opacity = '0.01';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        for (const item of state.items) item.el.style.opacity = '0';
        setState('lock');
        startMainLoop();
      }));
    });
}

/**
 * Decode every cover PNG into the browser's image cache. Returns a promise
 * that resolves when all decodes settle (failures are caught per-image so
 * one bad URL can't block the rest).
 */
async function preloadCovers(): Promise<void> {
  const coverUrls = APP_DATA
    .filter(a => typeof a.cover === 'string' && a.cover.length > 0)
    .map(a => a.cover as string);
  await Promise.all(coverUrls.map(src => {
    const img = new Image();
    img.src = src;
    return img.decode().catch(err => {
      console.warn('[Loop OS] cover decode failed:', src, err);
      return null;
    });
  }));
}

// ── Boot guard ────────────────────────────────────────────────────────────
// On Loop hardware, wait for the 'loop:ready' event before booting.
// On desktop, boot immediately.
if (hasLoop) {
  let started = false;
  function start() { if (started) return; started = true; init(); }
  window.addEventListener('loop:ready', start);
  setTimeout(start, 500);
} else {
  // DOMContentLoaded may have already fired by the time this module executes,
  // so call init() directly (Vite injects the module after DOM is ready).
  init();
}
